import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Dropdown from '../ui/Dropdown';
import { UploadIcon, PreviousIcon, NextIcon } from '../ui/Icons';
import ScoringPanel from '../ui/ScoringPanel';
import RallyPanel from '../ui/RallyPanel';

// The scoring panel is fully built but hidden while we decide whether to keep it.
// Flip to true to restore its tab; nothing else needs changing.
const SHOW_SCORING_PANEL = false;

// Demo video URL (using VIDEO:url,FPS:value format to avoid MediaInfo WASM dependency)
const DEMO_VIDEO_URL = 'VIDEO:https://splitstepclientvideos.blob.core.windows.net/client-videos/raw/client-internal/full_rights._clavel_vs_dan_martin_atp_450__4-6_18_nov_2025_720p_slice_2/demo_video.mp4?sp=racwdyti&st=2026-08-07T18:13:00Z&se=2030-01-02T03:28:00Z&spr=https&sv=2026-02-06&sr=b&sig=7uUSLL%2Bb5adeH%2B1OCvWCYYuYlc5fvWw5Gqb9nIlWDGQ%3D,FPS:30';

const SidebarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${theme.spacing.xl};
  z-index: 1000;
  flex-shrink: 0;
  width: 400px;
  height: 828px; /* Align with video area on large screens */
  padding-bottom: 72px; /* Clear the fixed logos / feedback row at the bottom */
  box-sizing: border-box;
  overflow-y: auto; /* Allow vertical scrolling when content exceeds height */
  overflow-x: hidden; /* Hide horizontal overflow */
  
  /* Fixed dimensions for smaller screens to align with video area */
  @media screen and (max-width: 1440px) {
    width: 400px;
    height: 648px;
    padding-bottom: 60px;
  }
`;

const TopButtonContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: center;
  width: 100%;
  flex-shrink: 0;
`;

const ControlCard = styled.div`
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  box-shadow: ${theme.shadows.md};
  font-family: ${theme.typography.fontFamily};
`;

const Form = styled.form`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  width: 100%;
`;

const UploadContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 0;
  height: 32px;
`;

const UploadBox = styled.label`
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: ${props => props.error
    ? `1px solid ${theme.colors.error}`
    : props.empty
      ? `1.5px dashed ${theme.colors.primary}`
      : `1px solid ${theme.colors.border}`};
  display: flex;
  align-items: center;
  gap: 0;
  box-shadow: ${theme.shadows.sm};
  min-height: 28px;
  width: 100%;
  cursor: pointer;
  transition: background-color ${theme.transitions.normal}, border-color ${theme.transitions.normal};

  &:hover {
    background-color: ${props => props.empty ? `${theme.colors.primary}10` : theme.colors.surfaceHover};
    border-color: ${props => props.empty ? theme.colors.primary : theme.colors.textSecondary};
  }
  box-sizing: border-box;
  flex-shrink: 0;
`;

const UploadButton = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.sm};
  transition: background-color ${theme.transitions.normal};
  width: 32px;
  flex-shrink: 0;
  justify-content: center;
  
  &:hover {
    background-color: ${theme.colors.surfaceHover};
  }
`;

const UploadContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: calc(100% - 32px);
`;

const UploadText = styled.span`
  color: ${props => props.error ? theme.colors.error : props.success ? theme.colors.success : theme.colors.textSecondary};
  font-size: ${theme.typography.fontSizes.base};
  font-weight: ${props => props.error ? theme.typography.fontWeights.medium : theme.typography.fontWeights.normal};
  font-family: ${theme.typography.fontFamily};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
  max-width: ${props => props.hasClear ? 'calc(100% - 50px)' : '100%'};
  margin-right: ${props => props.hasClear ? theme.spacing.sm : 0};
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.error};
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: ${theme.typography.fontSizes.base};
  cursor: pointer;
  padding: 0 ${theme.spacing.xs};
  font-family: ${theme.typography.fontFamily};
  flex-shrink: 0;
  min-width: 42px;
`;

const UploadVideoButton = styled.label`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${theme.colors.primaryDark};
  color: ${theme.colors.text};
  padding: 4px 10px;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  font-size: 13px;
  font-weight: ${theme.typography.fontWeights.medium};
  transition: ${theme.transitions.normal};
  border: 1px solid ${theme.colors.border};
  font-family: ${theme.typography.fontFamily};
  letter-spacing: ${theme.typography.letterSpacing.tight};
  box-shadow: ${theme.shadows.sm};
  
  &:hover {
    background-color: ${theme.colors.primary};
    box-shadow: ${theme.shadows.md};
  }
`;

const PlayerPositionCard = styled.div`
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  box-shadow: ${theme.shadows.md};
  font-family: ${theme.typography.fontFamily};
`;

const PlayerPositionTitle = styled.div`
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: ${theme.typography.fontSizes.lg};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.text};
  text-align: center;
  margin-bottom: ${theme.spacing.md};
  letter-spacing: ${theme.typography.letterSpacing.normal};
`;

const PlayerPositionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.sm};
`;

const PlayerPosition = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const PlayerDot = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${props => props.color};
  border: 2px solid ${props => props.color};
`;

const PlayerLabel = styled.span`
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.base};
  font-weight: ${theme.typography.fontWeights.medium};
  font-family: ${theme.typography.fontFamily};
`;

const CounterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const ModeRow = styled.div`
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: ${theme.typography.fontSizes.lg};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-bottom: ${theme.spacing.sm};
  border-bottom: 1px solid ${theme.colors.border};
  margin-bottom: ${theme.spacing.xs};
`;

const ModeLabel = styled.span`
  color: ${props => {
    switch (props.mode) {
      case 'stroke': return theme.colors.stroke;
      case 'bounce': return theme.colors.bounce;
      case 'in-flight': return theme.colors.inFlight;
      case 'net': return theme.colors.net;
      case 'court': return theme.colors.courtBaseline;
      case 'player': return theme.colors.playerMode;
      default: return theme.colors.text;
    }
  }};
  font-weight: ${theme.typography.fontWeights.bold};
  display: inline-block;
  width: 80px;
  text-align: left;
  margin-left: ${theme.spacing.xs};
`;

const CounterRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: ${theme.spacing.lg};
`;

const CounterItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex: 1;
  min-width: 0;
`;

const CounterDot = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${props => props.color};
  background-color: transparent;
`;

const CounterText = styled.span`
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.medium};
  font-family: ${theme.typography.fontFamily};
  white-space: nowrap;
  text-align: left;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  margin-left: auto;
  flex-shrink: 0;
`;

const NavigationButton = styled.button`
  width: 20px;
  height: 20px;
  padding: 0;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  &:hover:not(:disabled) {
    background-color: ${theme.colors.surfaceHover};
    border-radius: ${theme.borderRadius.sm};
  }
`;

const StrokeNavigationButton = styled(NavigationButton)`
  svg {
    stroke: ${theme.colors.stroke};
  }
`;

const BounceNavigationButton = styled(NavigationButton)`
  svg {
    stroke: ${theme.colors.bounce};
  }
`;

const InFlightNavigationButton = styled(NavigationButton)`
  svg {
    stroke: ${theme.colors.inFlight};
  }
`;

const NetNavigationButton = styled(NavigationButton)`
  svg {
    stroke: ${theme.colors.net};
  }
`;

const CourtNavigationButton = styled(NavigationButton)`
  svg {
    stroke: ${theme.colors.courtBaseline};
  }
`;

const PlusSign = styled.span`
  color: ${theme.colors.text};
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: ${theme.typography.fontSizes.lg};
  margin: 0 ${theme.spacing.sm};
  background: none;
  border: none;
  padding: 0;
`;

const CheckboxContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
`;

const CustomCheckbox = styled.div`
  position: relative;
  width: 16px;
  height: 16px;
  border: 2px solid ${props => props.checked ? theme.colors.primary : theme.colors.textSecondary};
  background-color: ${props => props.checked ? theme.colors.primary : 'transparent'};
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${theme.colors.primary};
  }
  
  &::after {
    content: '';
    position: absolute;
    display: ${props => props.checked ? 'block' : 'none'};
    left: 4px;
    top: 1px;
    width: 4px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const Spinner = styled.div`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid ${theme.colors.textSecondary};
  border-top-color: ${theme.colors.primary};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: ${theme.spacing.xs};
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ReviewNavigationButton = styled(NavigationButton)`
  svg {
    stroke: #F8F8F2;
  }
`;

const RallyNavigationButton = styled(NavigationButton)`
  svg {
    stroke: #F8F8F2;
  }
`;

const ReviewHelpTooltipContent = styled.div`
  position: absolute;
  right: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  background-color: ${theme.colors.background};
  color: ${theme.colors.text};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSizes.sm};
  line-height: 1.5;
  white-space: normal;
  width: 248px;
  border: 1px solid ${theme.colors.border};
  box-shadow: ${theme.shadows.lg};
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  z-index: 20;
  pointer-events: none;
`;

const ReviewHelpTooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: auto;

  &:hover ${ReviewHelpTooltipContent},
  &:focus-within ${ReviewHelpTooltipContent} {
    opacity: 1;
    visibility: visible;
  }
`;

const ReviewHelpTooltipTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background-color: ${theme.colors.border};
  color: ${theme.colors.text};
  font-size: 12px;
  font-weight: ${theme.typography.fontWeights.bold};
  font-family: ${theme.typography.fontFamily};
  line-height: 1;
  cursor: help;
  flex-shrink: 0;

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

const LabelTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: ${theme.borderRadius.sm};
  background-color: rgba(40, 42, 54, 0.7);
  border: 1px solid ${theme.colors.border};
  font-size: ${theme.typography.fontSizes.sm};
  font-family: ${theme.typography.fontFamily};
  color: #F8F8F2;
`;

const LabelRemoveButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.error};
  font-size: ${theme.typography.fontSizes.sm};
  cursor: pointer;
  padding: 0;
  line-height: 1;
  font-weight: ${theme.typography.fontWeights.bold};
`;

const SendVideoButton = styled.button`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: ${theme.typography.fontSizes.base};
  font-weight: ${theme.typography.fontWeights.medium};
  font-family: ${theme.typography.fontFamily};
  background: ${props => props.disabled ? theme.colors.surface : `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`};
  background-clip: padding-box;
  border: 1px solid ${props => props.disabled ? theme.colors.border : 'transparent'};
  border-radius: ${theme.borderRadius.md};
  color: ${props => props.disabled ? theme.colors.textMuted : 'white'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: ${theme.transitions.normal};
  
  &:hover:not(:disabled) {
    opacity: 0.9;
    box-shadow: ${theme.shadows.md};
  }
  
  &:focus {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

const Sidebar = ({
  videoUrl,
  onVideoUrlChange,
  onVideoUrlSubmit,
  onVideoFileUpload,
  onLoadVideo,
  videoError,
  annotationMode,
  annotationCounts,
  annotationProgress,
  navigateToAnnotation,
  loadedFileName,
  uploadError,
  onAnnotationUpload,
  onClearAnnotations,
  currentTime,
  annotations,
  playerAnnotations = [],
  courtKeypoints,
  isCourtMode,
  isPlayerMode,
  isRallyMode,
  // Everything the rally scoring panel needs, grouped to keep this signature
  // readable and to avoid colliding with the stroke-derived `rallyGroups`.
  rallyScoring,
  navigateToCourtKeypoint,
  playerFilter,
  onPlayerFilterChange,
  strokeTypeFilter,
  onStrokeTypeFilterChange,
  volleyFilter = false,
  onVolleyFilterChange,
  volleyProgress = { current: 0, total: 0, sortedTimestamps: [] },
  getCurrentAnnotationData,
  saveCourtCoordinates,
  onSaveCourtCoordinatesChange,
  showTrajectories = false,
  onShowTrajectoriesChange,
  showCall = false,
  onShowCallChange,
  onRecalibrateCamera,
  isProcessingCamera = false,
  onStartTutorial,
  onTutorialActionComplete,
  onLogout,
  onAzurePopupOpen,
  onBillingClick,
  onSendVideoForProcessing,
  hasVideoLoaded = false,
  videoProcessingRemaining = null,
  reviewCounts = { total: 0, labeled: 0, remaining: 0 },
  navigateToReview,
  rallyGroups = [],
  navigateToRally,
  navigateToCustomLabel,
  customLabels = [],
  onAddCustomLabel,
  onRemoveCustomLabel,
  currentAnnotationLabel,
  activeRallyStartTime,
  onSeek,
  onMergeWithPrevious,
  onAddPointAfter,
  onTogglePointWinner,
  onToggleGameWinner,
  onToggleSetWinner,
  onSplitGame,
  onMergeGameWithPrevious,
}) => {
  const [newLabelInput, setNewLabelInput] = useState('');
  const [panelMode, setPanelMode] = useState('annotations');
  const [jumpTarget, setJumpTarget] = useState('end');
  // Pre-compute sorted timestamps per annotation type so that navigation
  // button disabled/opacity checks are O(1) per render instead of O(n).
  const typeBoundaries = useMemo(() => {
    const result = {};
    for (const type of ['stroke', 'bounce', 'in-flight', 'net']) {
      if (type === 'stroke' && volleyFilter) {
        const ts = volleyProgress.sortedTimestamps;
        result[type] = ts.length > 0 ? { min: ts[0], max: ts[ts.length - 1] } : null;
        continue;
      }
      let filtered;
      if (type === 'stroke') {
        filtered = annotations.filter(a =>
          a.type === type &&
          (playerFilter === 'all' || a.player === playerFilter) &&
          (strokeTypeFilter === 'all' || a.strokeType === strokeTypeFilter)
        );
      } else {
        filtered = annotations.filter(a =>
          a.type === type &&
          (playerFilter === 'all' || a.player === playerFilter)
        );
      }
      if (filtered.length > 0) {
        const sorted = filtered.map(a => a.timestamp).sort((a, b) => a - b);
        result[type] = { min: sorted[0], max: sorted[sorted.length - 1] };
      } else {
        result[type] = null;
      }
    }
    return result;
  }, [annotations, playerFilter, strokeTypeFilter, volleyFilter, volleyProgress]);

  const courtBounds = useMemo(() => {
    if (!courtKeypoints) return null;
    const nonReprojected = courtKeypoints.filter(c => !c.reprojected);
    if (nonReprojected.length === 0) return null;
    const sorted = nonReprojected.map(c => c.timestamp).sort((a, b) => a - b);
    return { min: sorted[0], max: sorted[sorted.length - 1], sortedTs: sorted, total: sorted.length };
  }, [courtKeypoints]);

  const reviewBounds = useMemo(() => {
    const flagged = annotations.filter(a => a.flaggedForReview);
    if (flagged.length === 0) return null;
    const sorted = flagged.map(a => a.timestamp).sort((a, b) => a - b);
    return { min: sorted[0], max: sorted[sorted.length - 1] };
  }, [annotations]);

  const rallyBounds = useMemo(() => {
    if (rallyGroups.length === 0) return null;
    return { min: rallyGroups[0].startTime, max: rallyGroups[rallyGroups.length - 1].startTime };
  }, [rallyGroups]);

  const rallyProgress = useMemo(() => {
    const total = rallyGroups.length;
    let current = 0;
    for (const r of rallyGroups) {
      if (r.startTime <= currentTime) current++;
      else break;
    }
    return { current, total };
  }, [rallyGroups, currentTime]);

  const customLabelBounds = useMemo(() => {
    const result = {};
    for (const label of customLabels) {
      const matches = annotations.filter(a => a.customLabel === label);
      if (matches.length === 0) {
        result[label] = { count: 0, min: null, max: null };
      } else {
        const sorted = matches.map(a => a.timestamp).sort((a, b) => a - b);
        result[label] = {
          count: matches.length,
          min: sorted[0],
          max: sorted[sorted.length - 1],
        };
      }
    }
    return result;
  }, [annotations, customLabels]);

  const hasPrev = (type) => typeBoundaries[type] && typeBoundaries[type].min < currentTime;
  const hasNext = (type) => typeBoundaries[type] && typeBoundaries[type].max > currentTime;
  const hasReviewPrev = reviewBounds && reviewBounds.min < currentTime;
  const hasReviewNext = reviewBounds && reviewBounds.max > currentTime;
  const hasRallyPrev  = rallyBounds  && rallyBounds.min  < currentTime;
  const hasRallyNext  = rallyBounds  && rallyBounds.max  > currentTime;

  const hasCustomLabelPrev = (label) => {
    const b = customLabelBounds[label];
    return b && b.min != null && b.min < currentTime;
  };
  const hasCustomLabelNext = (label) => {
    const b = customLabelBounds[label];
    return b && b.max != null && b.max > currentTime;
  };

  // Binary search: count of sorted timestamps <= currentTime
  const courtCountAtTime = useMemo(() => {
    if (!courtBounds) return 0;
    const ts = courtBounds.sortedTs;
    let lo = 0, hi = ts.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (ts[mid] <= currentTime) lo = mid + 1; else hi = mid;
    }
    return lo;
  }, [courtBounds, currentTime]);

  const handleDemoVideoClick = async () => {
    if (onLoadVideo) {
      await onLoadVideo(DEMO_VIDEO_URL, 'demo_video.mp4');
      // Notify tutorial if active
      if (onTutorialActionComplete) {
        setTimeout(() => {
          onTutorialActionComplete();
        }, 1000);
      }
    }
  };

  const getCurrentAnnotations = () => {
    // Use the passed function from VideoPlayer to generate annotation data
    if (getCurrentAnnotationData) {
      return getCurrentAnnotationData();
    }
    return null;
  };

  const playerFilterOptions = [
    { value: 'all', label: 'All', color: theme.colors.text },
    { value: 'bottom', label: 'Bottom', color: '#50fa7b' },
    { value: 'top', label: 'Top', color: '#ff5555' },
  ];

  const strokeTypeFilterOptions = [
    { value: 'all', label: 'All', color: theme.colors.text },
    { value: 'forehand', label: 'Forehand', color: '#52c3a3' },
    { value: 'backhand', label: 'Backhand', color: '#db722c' },
    { value: 'serve', label: 'Serve', color: '#f8f9f2' },
    { value: 'smash', label: 'Smash', color: '#f1fa8c' },
  ];

  return (
    <SidebarContainer data-tutorial="sidebar">
      <div data-tutorial="video-loading-section" style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xl }}>
        <TopButtonContainer>
          <Button onClick={onLogout} style={{ flex: '0 0 auto' }}>
            Logout
          </Button>
          <Button onClick={onStartTutorial} style={{ flex: '0 0 auto' }}>
            Tutorial
          </Button>
          <Button onClick={onBillingClick} style={{ flex: '0 0 auto' }}>
            Billing
          </Button>
          <Button
            onClick={onAzurePopupOpen}
            style={{ flex: 1 }}
            data-tutorial="azure-storage"
          >
            Azure
          </Button>
        </TopButtonContainer>

        {/* Video URL Input */}
        <ControlCard style={{ minHeight: '100px' }}>
          <Form onSubmit={onVideoUrlSubmit}>
            <Input
              type="text"
              value={videoUrl}
              onChange={onVideoUrlChange}
              placeholder="Paste video URL here"
              style={{ flex: 1 }}
            />
          </Form>
          <div style={{ display: 'flex', gap: '8px', width: '100%', marginTop: '8px' }}>
            <Button
              type="button"
              onClick={onVideoUrlSubmit}
              style={{
                flex: 1,
                padding: '4px 10px',
                fontSize: '13px'
              }}
            >
              Load Video
            </Button>
            <UploadVideoButton htmlFor="video-file-input">
              Upload Video
            </UploadVideoButton>
            <Button
              type="button"
              onClick={handleDemoVideoClick}
              data-tutorial="demo-video"
              style={{
                flex: 1,
                padding: '4px 10px',
                fontSize: '13px'
              }}
            >
              Demo Video
            </Button>
          </div>
          <input
            type="file"
            accept="video/*"
            onChange={onVideoFileUpload}
            style={{ display: 'none' }}
            id="video-file-input"
          />
          {videoError && !videoError.includes('Analyzing video metadata') && (
            <div style={{
              marginTop: '6px',
              padding: '4px 8px',
              backgroundColor: theme.colors.error,
              color: 'white',
              borderRadius: '3px',
              fontSize: '11px',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              {videoError}
            </div>
          )}
        </ControlCard>

        {/* Send Video for Processing Button */}
        <SendVideoButton
          onClick={onSendVideoForProcessing}
          disabled={!hasVideoLoaded || videoProcessingRemaining === 0}
          title={
            !hasVideoLoaded 
              ? 'Load a video first' 
              : videoProcessingRemaining === 0 
                ? 'Video processing limit reached - upgrade to Pro' 
                : 'Send video for processing'
          }
        >
          Send Video for Processing
          {videoProcessingRemaining !== null && videoProcessingRemaining !== undefined && ` (${videoProcessingRemaining} left)`}
        </SendVideoButton>
      </div>

      {/* Upload Annotations */}
      <UploadContainer data-tutorial="upload-annotations">
        <UploadBox error={uploadError} empty={!loadedFileName}>
          <input
            type="file"
            accept="application/json"
            style={{ display: 'none' }}
            onChange={onAnnotationUpload}
          />
          <UploadButton as="span">
            <UploadIcon />
          </UploadButton>
          <UploadContent>
            {uploadError ? (
              <UploadText error>{uploadError}</UploadText>
            ) : loadedFileName ? (
              <>
                <UploadText success hasClear>
                  Loaded: {loadedFileName}
                </UploadText>
                <ClearButton onClick={(e) => { e.preventDefault(); onClearAnnotations(); }}>
                  Clear
                </ClearButton>
              </>
            ) : (
              <UploadText style={{ color: theme.colors.primary, fontWeight: theme.typography.fontWeights.medium }}>
                Upload Annotations
              </UploadText>
            )}
          </UploadContent>
        </UploadBox>
      </UploadContainer>

      {/* Annotation Counters / Scoring — tabbed panel */}
      <ControlCard style={{
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        overflow: 'hidden',
        // Rally mode stacks names, match rules, the mark button and a footer
        // above the list; it fills the rest of the sidebar and the list scrolls internally.
        ...(isRallyMode ? { flex: 1, minHeight: 0 } : { height: '360px' }),
      }}>
        {/* Tab bar — pointless with a single tab, so it hides along with scoring */}
        <div style={{
          display: SHOW_SCORING_PANEL && !isRallyMode ? 'flex' : 'none',
          flexShrink: 0,
          borderBottom: `1px solid ${theme.colors.border}`,
        }}>
          {['annotations', 'scoring'].map((mode) => (
            <button
              key={mode}
              onClick={() => setPanelMode(mode)}
              style={{
                flex: 1,
                padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                border: 'none',
                background: panelMode === mode ? theme.colors.background : 'transparent',
                color: panelMode === mode ? theme.colors.primary : theme.colors.textMuted,
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.fontSizes.sm,
                fontWeight: panelMode === mode
                  ? theme.typography.fontWeights.semibold
                  : theme.typography.fontWeights.normal,
                cursor: 'pointer',
                borderBottom: panelMode === mode
                  ? `2px solid ${theme.colors.primary}`
                  : '2px solid transparent',
                transition: 'all 0.15s',
                textTransform: 'capitalize',
              }}
            >
              {mode}
            </button>
          ))}
        </div>

        {/* Annotations content */}
        <div style={{
          display: !isRallyMode && panelMode === 'annotations' ? 'block' : 'none',
          overflowY: 'auto',
          flex: 1,
          padding: theme.spacing.md,
        }}>
        <CounterContainer>
          {/* Current Mode / Label Row */}
          <ModeRow>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', gap: theme.spacing.md }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                <span>Mode:</span>
                <ModeLabel mode={isCourtMode ? 'court' : isPlayerMode ? 'player' : annotationMode}>
                  {isCourtMode ? 'Court' : isPlayerMode ? 'Player' : annotationMode.charAt(0).toUpperCase() + annotationMode.slice(1)}
                </ModeLabel>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                <span>Label:</span>
                <span style={{ color: '#F8F8F2', fontWeight: theme.typography.fontWeights.bold, display: 'inline-block', width: '80px', textAlign: 'left' }}>
                  {currentAnnotationLabel && currentAnnotationLabel !== 'none' ? currentAnnotationLabel : '—'}
                </span>
              </div>
            </div>
          </ModeRow>

          {/* Filters Row */}
          {!isCourtMode && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.md,
              paddingBottom: theme.spacing.sm,
              borderBottom: `1px solid ${theme.colors.border}`,
              marginBottom: theme.spacing.xs
            }}>
              <div data-tutorial="filters" style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                <span style={{ fontSize: theme.typography.fontSizes.sm }}>Player:</span>
                <Dropdown
                  options={playerFilterOptions}
                  value={playerFilter}
                  onChange={onPlayerFilterChange}
                  placeholder="Select player filter"
                />
              </div>
              <div data-tutorial="filters" style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                <span style={{ fontSize: theme.typography.fontSizes.sm }}>Type:</span>
                <Dropdown
                  options={strokeTypeFilterOptions}
                  value={strokeTypeFilter}
                  onChange={onStrokeTypeFilterChange}
                  placeholder="Select stroke type filter"
                />
              </div>
            </div>
          )}

          {!isCourtMode ? (
            // Ball mode - show ball annotation counters in 2x2 grid
            <>
              <CounterRow>
                <CounterItem>
                  <CounterDot color={theme.colors.stroke} />
                  <CounterText>Strokes: {annotationProgress.stroke.current}/{annotationProgress.stroke.total}</CounterText>
                  <NavigationButtons>
                    <StrokeNavigationButton
                      disabled={!hasPrev('stroke')}
                      onClick={() => navigateToAnnotation('stroke', 'previous', playerFilter, strokeTypeFilter)}
                      title="Previous Stroke"
                      style={{ opacity: hasPrev('stroke') ? 1 : 0.4 }}
                    >
                      <PreviousIcon />
                    </StrokeNavigationButton>
                    <StrokeNavigationButton
                      disabled={!hasNext('stroke')}
                      onClick={() => navigateToAnnotation('stroke', 'next', playerFilter, strokeTypeFilter)}
                      title="Next Stroke"
                      style={{ opacity: hasNext('stroke') ? 1 : 0.4 }}
                    >
                      <NextIcon />
                    </StrokeNavigationButton>
                  </NavigationButtons>
                </CounterItem>
                <CounterItem>
                  <CounterDot color={theme.colors.bounce} />
                  <CounterText>Bounces: {annotationProgress.bounce.current}/{annotationProgress.bounce.total}</CounterText>
                  <NavigationButtons>
                    <BounceNavigationButton
                      disabled={!hasPrev('bounce')}
                      onClick={() => navigateToAnnotation('bounce', 'previous', playerFilter, strokeTypeFilter)}
                      title="Previous Bounce"
                      style={{ opacity: hasPrev('bounce') ? 1 : 0.4 }}
                    >
                      <PreviousIcon />
                    </BounceNavigationButton>
                    <BounceNavigationButton
                      disabled={!hasNext('bounce')}
                      onClick={() => navigateToAnnotation('bounce', 'next', playerFilter, strokeTypeFilter)}
                      title="Next Bounce"
                      style={{ opacity: hasNext('bounce') ? 1 : 0.4 }}
                    >
                      <NextIcon />
                    </BounceNavigationButton>
                  </NavigationButtons>
                </CounterItem>
              </CounterRow>

              <CounterRow>
                <CounterItem>
                  <CounterDot color={theme.colors.inFlight} />
                  <CounterText>In-Flight: {annotationProgress.inFlight.current}/{annotationProgress.inFlight.total}</CounterText>
                  <NavigationButtons>
                    <InFlightNavigationButton
                      disabled={!hasPrev('in-flight')}
                      onClick={() => navigateToAnnotation('in-flight', 'previous', playerFilter, strokeTypeFilter)}
                      title="Previous In-Flight"
                      style={{ opacity: hasPrev('in-flight') ? 1 : 0.4 }}
                    >
                      <PreviousIcon />
                    </InFlightNavigationButton>
                    <InFlightNavigationButton
                      disabled={!hasNext('in-flight')}
                      onClick={() => navigateToAnnotation('in-flight', 'next', playerFilter, strokeTypeFilter)}
                      title="Next In-Flight"
                      style={{ opacity: hasNext('in-flight') ? 1 : 0.4 }}
                    >
                      <NextIcon />
                    </InFlightNavigationButton>
                  </NavigationButtons>
                </CounterItem>
                <CounterItem>
                  <CounterDot color={theme.colors.net} />
                  <CounterText>Net: {annotationProgress.net.current}/{annotationProgress.net.total}</CounterText>
                  <NavigationButtons>
                    <NetNavigationButton
                      disabled={!hasPrev('net')}
                      onClick={() => navigateToAnnotation('net', 'previous', playerFilter, strokeTypeFilter)}
                      title="Previous Net"
                      style={{ opacity: hasPrev('net') ? 1 : 0.4 }}
                    >
                      <PreviousIcon />
                    </NetNavigationButton>
                    <NetNavigationButton
                      disabled={!hasNext('net')}
                      onClick={() => navigateToAnnotation('net', 'next', playerFilter, strokeTypeFilter)}
                      title="Next Net"
                      style={{ opacity: hasNext('net') ? 1 : 0.4 }}
                    >
                      <NextIcon />
                    </NetNavigationButton>
                  </NavigationButtons>
                </CounterItem>
              </CounterRow>

              {/* Rally + Review Counter Row */}
              <CounterRow style={{
                paddingTop: theme.spacing.sm,
                borderTop: `1px solid ${theme.colors.border}`,
                marginTop: theme.spacing.xs
              }}>
                <CounterItem>
                  <CounterText>Rally: {rallyProgress.current}/{rallyProgress.total}</CounterText>
                  <NavigationButtons>
                    <RallyNavigationButton
                      disabled={!hasRallyPrev}
                      onClick={() => navigateToRally && navigateToRally('previous', jumpTarget)}
                      title="Previous rally"
                      style={{ opacity: hasRallyPrev ? 1 : 0.4 }}
                    >
                      <PreviousIcon />
                    </RallyNavigationButton>
                    <RallyNavigationButton
                      disabled={!hasRallyNext}
                      onClick={() => navigateToRally && navigateToRally('next', jumpTarget)}
                      title="Next rally"
                      style={{ opacity: hasRallyNext ? 1 : 0.4 }}
                    >
                      <NextIcon />
                    </RallyNavigationButton>
                  </NavigationButtons>
                </CounterItem>
                <CounterItem>
                  <CounterText>To Review: {annotationProgress.review?.current || 0}/{annotationProgress.review?.total || 0}</CounterText>
                  <NavigationButtons>
                    <ReviewNavigationButton
                      disabled={!hasReviewPrev}
                      onClick={() => navigateToReview && navigateToReview('previous')}
                      title="Previous flagged"
                      style={{ opacity: hasReviewPrev ? 1 : 0.4 }}
                    >
                      <PreviousIcon />
                    </ReviewNavigationButton>
                    <ReviewNavigationButton
                      disabled={!hasReviewNext}
                      onClick={() => navigateToReview && navigateToReview('next')}
                      title="Next flagged"
                      style={{ opacity: hasReviewNext ? 1 : 0.4 }}
                    >
                      <NextIcon />
                    </ReviewNavigationButton>
                  </NavigationButtons>
                </CounterItem>
                <CounterItem style={{ justifyContent: 'flex-end', flex: '0 0 auto' }}>
                  <ReviewHelpTooltipWrapper>
                    <ReviewHelpTooltipTrigger type="button" aria-label="About review and custom labels">
                      ?
                    </ReviewHelpTooltipTrigger>
                    <ReviewHelpTooltipContent>
                      Press R to flag the current annotation for review, then use the arrows to step through
                      flagged items. Add custom labels below when the built-in types are not enough.
                    </ReviewHelpTooltipContent>
                  </ReviewHelpTooltipWrapper>
                </CounterItem>
              </CounterRow>

              {/* Custom Labels — scrolls within the card's maxHeight */}
              <div style={{
                paddingTop: theme.spacing.sm,
                borderTop: `1px solid ${theme.colors.border}`,
                marginTop: theme.spacing.xs,
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing.xs,
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing.sm,
                  flexShrink: 0,
                }}>
                  <input
                    type="text"
                    value={newLabelInput}
                    onChange={(e) => setNewLabelInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        if (newLabelInput.trim()) {
                          onAddCustomLabel && onAddCustomLabel(newLabelInput.trim());
                          setNewLabelInput('');
                        }
                      }
                    }}
                    placeholder="Add label (e.g. FP)"
                    style={{
                      flex: 1,
                      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                      fontSize: theme.typography.fontSizes.sm,
                      fontFamily: theme.typography.fontFamily,
                      backgroundColor: theme.colors.surface,
                      color: theme.colors.text,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.sm,
                      outline: 'none',
                    }}
                  />
                  <Button
                    onClick={() => {
                      if (newLabelInput.trim()) {
                        onAddCustomLabel && onAddCustomLabel(newLabelInput.trim());
                        setNewLabelInput('');
                      }
                    }}
                    style={{
                      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                      fontSize: theme.typography.fontSizes.sm,
                      flexShrink: 0,
                    }}
                  >
                    Add
                  </Button>
                </div>
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                  {customLabels.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing.xs, alignItems: 'center' }}>
                      {customLabels.map(label => (
                        <div
                          key={label}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 2,
                          }}
                        >
                          <ReviewNavigationButton
                            type="button"
                            disabled={!hasCustomLabelPrev(label)}
                            onClick={() => navigateToCustomLabel && navigateToCustomLabel(label, 'previous')}
                            title={`Previous annotation labeled "${label}"`}
                            style={{ opacity: hasCustomLabelPrev(label) ? 1 : 0.4 }}
                          >
                            <PreviousIcon />
                          </ReviewNavigationButton>
                          <LabelTag>
                            <span>{label}</span>
                            <span
                              style={{
                                fontSize: theme.typography.fontSizes.xs,
                                color: theme.colors.textSecondary,
                                fontWeight: theme.typography.fontWeights.normal,
                              }}
                              title={`${customLabelBounds[label]?.count ?? 0} annotation(s) with this label`}
                            >
                              {customLabelBounds[label]?.count ?? 0}
                            </span>
                            <LabelRemoveButton
                              onClick={() => onRemoveCustomLabel && onRemoveCustomLabel(label)}
                              title={`Remove label "${label}"`}
                            >
                              ×
                            </LabelRemoveButton>
                          </LabelTag>
                          <ReviewNavigationButton
                            type="button"
                            disabled={!hasCustomLabelNext(label)}
                            onClick={() => navigateToCustomLabel && navigateToCustomLabel(label, 'next')}
                            title={`Next annotation labeled "${label}"`}
                            style={{ opacity: hasCustomLabelNext(label) ? 1 : 0.4 }}
                          >
                            <NextIcon />
                          </ReviewNavigationButton>
                        </div>
                      ))}
                    </div>
                  )}
                  {customLabels.length > 0 && (
                    <div style={{
                      fontSize: theme.typography.fontSizes.xs,
                      color: theme.colors.textSecondary,
                      flexShrink: 0,
                    }}>
                      Press <strong>G</strong> to cycle labels
                    </div>
                  )}
                </div>
              </div>

            </>
          ) : (
            // Court mode - show court keypoints counter in 2x2 grid (with empty spaces to maintain layout)
            <>
              <CounterRow>
                <CounterItem>
                  <CounterDot color={theme.colors.courtBaseline} />
                  <CounterText>Courts: {courtCountAtTime}/{courtBounds ? courtBounds.total : 0}</CounterText>
                  <NavigationButtons>
                    <CourtNavigationButton
                      disabled={!courtBounds || courtBounds.min >= currentTime}
                      onClick={() => navigateToCourtKeypoint('previous')}
                      title="Previous Court"
                      style={{ opacity: courtBounds && courtBounds.min < currentTime ? 1 : 0.4 }}
                    >
                      <PreviousIcon />
                    </CourtNavigationButton>
                    <CourtNavigationButton
                      disabled={!courtBounds || courtBounds.max <= currentTime}
                      onClick={() => navigateToCourtKeypoint('next')}
                      title="Next Court"
                      style={{ opacity: courtBounds && courtBounds.max > currentTime ? 1 : 0.4 }}
                    >
                      <NextIcon />
                    </CourtNavigationButton>
                  </NavigationButtons>
                </CounterItem>
                <CounterItem>
                  {/* Empty space to maintain 2x2 grid */}
                </CounterItem>
              </CounterRow>

              <CounterRow>
                <CounterItem>
                  {/* Empty space to maintain 2x2 grid */}
                </CounterItem>
                <CounterItem>
                  {/* Empty space to maintain 2x2 grid */}
                </CounterItem>
              </CounterRow>

            </>
          )}
        </CounterContainer>
        </div>{/* end annotations content */}

        {/* Pinned footer: AI Assist (ball/player mode) or Recalibrate (court mode) */}
        {!isRallyMode && panelMode === 'annotations' && (
          <div style={{
            flexShrink: 0,
            borderTop: `1px solid ${theme.colors.border}`,
            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
          }}>
            {!isCourtMode ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                <div
                  data-tutorial="ai-assist"
                  style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}
                >
                  <CheckboxContainer>
                    <HiddenCheckbox
                      type="checkbox"
                      id="ai-assist"
                      checked={saveCourtCoordinates}
                      onChange={onSaveCourtCoordinatesChange}
                    />
                    <CustomCheckbox
                      checked={saveCourtCoordinates}
                      onClick={() => onSaveCourtCoordinatesChange({ target: { checked: !saveCourtCoordinates } })}
                    />
                    <label
                      htmlFor="ai-assist"
                      style={{
                        fontSize: theme.typography.fontSizes.sm,
                        color: theme.colors.text,
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                    >
                      AI Assist
                    </label>
                  </CheckboxContainer>
                  <CheckboxContainer>
                    <HiddenCheckbox
                      type="checkbox"
                      id="overlay-trajectories"
                      checked={showTrajectories}
                      onChange={(e) => onShowTrajectoriesChange && onShowTrajectoriesChange(e.target.checked)}
                    />
                    <CustomCheckbox
                      checked={showTrajectories}
                      onClick={() => onShowTrajectoriesChange && onShowTrajectoriesChange(!showTrajectories)}
                    />
                    <label
                      htmlFor="overlay-trajectories"
                      style={{
                        fontSize: theme.typography.fontSizes.sm,
                        color: theme.colors.text,
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                    >
                      Overlay Trajectories
                    </label>
                  </CheckboxContainer>
                  <CheckboxContainer>
                    <HiddenCheckbox
                      type="checkbox"
                      id="show-call"
                      checked={showCall}
                      onChange={(e) => onShowCallChange && onShowCallChange(e.target.checked)}
                    />
                    <CustomCheckbox
                      checked={showCall}
                      onClick={() => onShowCallChange && onShowCallChange(!showCall)}
                    />
                    <label
                      htmlFor="show-call"
                      style={{
                        fontSize: theme.typography.fontSizes.sm,
                        color: theme.colors.text,
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                    >
                      Call
                    </label>
                  </CheckboxContainer>
                  <CheckboxContainer>
                    <HiddenCheckbox
                      type="checkbox"
                      id="volley-filter"
                      checked={volleyFilter}
                      onChange={(e) => onVolleyFilterChange && onVolleyFilterChange(e.target.checked)}
                    />
                    <CustomCheckbox
                      checked={volleyFilter}
                      onClick={() => onVolleyFilterChange && onVolleyFilterChange(!volleyFilter)}
                    />
                    <label
                      htmlFor="volley-filter"
                      style={{
                        fontSize: theme.typography.fontSizes.sm,
                        color: theme.colors.text,
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                    >
                      Volleys{volleyProgress.total > 0 ? ` (${volleyProgress.total})` : ''}
                    </label>
                  </CheckboxContainer>
                </div>
              </div>
            ) : (
              saveCourtCoordinates && courtKeypoints && courtKeypoints.filter(c => !c.reprojected).length > 0 && (
                <Button
                  onClick={onRecalibrateCamera}
                  disabled={isProcessingCamera || !onRecalibrateCamera}
                  style={{
                    width: '100%',
                    fontSize: theme.typography.fontSizes.sm,
                    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                    opacity: isProcessingCamera ? 0.7 : 1,
                    transition: 'opacity 0.2s ease',
                  }}
                >
                  {isProcessingCamera && <Spinner />}
                  {isProcessingCamera ? 'Recalibrating...' : 'Recalibrate'}
                </Button>
              )
            )}
          </div>
        )}

        {/* Scoring tree content — kept mounted to preserve expansion state */}
        <div style={{
          display: SHOW_SCORING_PANEL && panelMode === 'scoring' ? 'flex' : 'none',
          flex: 1,
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}>
          <ScoringPanel
            embedded
            annotations={annotations}
            activeStartTime={activeRallyStartTime}
            jumpTarget={jumpTarget}
            onJumpTargetChange={setJumpTarget}
            onSeek={onSeek}
            onMergeWithPrevious={onMergeWithPrevious}
            onAddPointAfter={onAddPointAfter}
            onTogglePointWinner={onTogglePointWinner}
            onToggleGameWinner={onToggleGameWinner}
            onToggleSetWinner={onToggleSetWinner}
            onSplitGame={onSplitGame}
            onMergeGameWithPrevious={onMergeGameWithPrevious}
          />
        </div>

        {/* Rally scoring content — unmounted outside rally mode so its
            per-frame pending-rally readout costs nothing the rest of the time */}
        {isRallyMode && rallyScoring && (
          <div style={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
          }}>
            <RallyPanel {...rallyScoring} currentTime={currentTime} onSeek={onSeek} />
          </div>
        )}
      </ControlCard>

      {/* Keyboard Shortcuts Hint */}
      {!isRallyMode && (
        <div
          data-tutorial="shortcuts-hint"
          style={{
            textAlign: 'center',
            padding: '16px 12px',
            color: theme.colors.accent,
            fontSize: '14px',
            fontFamily: 'Roboto, sans-serif',
            fontWeight: '500',
            backgroundColor: 'rgba(114, 210, 255, 0.08)',
            borderRadius: '8px',
            border: `1px solid ${theme.colors.border}`,
          }}
        >
          💡 Press <strong>Shift+K</strong> for keyboard shortcuts
        </div>
      )}

    </SidebarContainer>
  );
};

export default Sidebar; 